import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  MoreHorizontal,
  FileMinus,
  Loader2,
  Eye,
  Pencil,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  Send,
  Link2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';

const statusConfig: Record<string, { color: string; icon: React.ElementType }> = {
  DRAFT: { color: 'bg-gray-100 text-gray-700', icon: Clock },
  ISSUED: { color: 'bg-blue-100 text-blue-700', icon: Send },
  PARTIALLY_APPLIED: { color: 'bg-yellow-100 text-yellow-700', icon: Link2 },
  FULLY_APPLIED: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  CANCELLED: { color: 'bg-gray-100 text-gray-500', icon: XCircle },
};

export function CreditNotesPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const [debouncedSearch, setDebouncedSearch] = useSessionState(
    'trading:credit-notes:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [selectedStatus, setSelectedStatus] = useSessionState<string>(
    'trading:credit-notes:selectedStatus',
    'all'
  );
  const [page, setPage] = useState(1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data, isLoading, error } = trpc.tradingCreditNote.list.useQuery({
    search: debouncedSearch || undefined,
    status: selectedStatus !== 'all' ? (selectedStatus as any) : undefined,
    page,
  });

  const utils = trpc.useUtils();

  const cancelMutation = trpc.tradingCreditNote.cancel.useMutation({
    onSuccess: () => {
      utils.tradingCreditNote.list.invalidate();
      showSuccessToast(t('finance.creditNotes.cancelled'));
      setDeleteConfirmOpen(false);
      setNoteToDelete(null);
    },
    onError: (error: { message: string }) => {
      showErrorToast(error.message);
    },
  });

  const issueMutation = trpc.tradingCreditNote.issue.useMutation({
    onSuccess: () => {
      utils.tradingCreditNote.list.invalidate();
      showSuccessToast(t('finance.creditNotes.issued'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const creditNotes = data?.creditNotes ?? [];
  const pagination = data?.pagination;

  const handleDeleteNote = (id: string) => {
    setNoteToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (noteToDelete) {
      cancelMutation.mutate({ id: noteToDelete, reason: 'Cancelled by user' });
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return t('finance.creditNotes.statusDraft');
      case 'ISSUED':
        return t('finance.creditNotes.statusIssued');
      case 'PARTIALLY_APPLIED':
        return t('finance.creditNotes.statusPartiallyApplied');
      case 'FULLY_APPLIED':
        return t('finance.creditNotes.statusFullyApplied');
      case 'CANCELLED':
        return t('finance.creditNotes.statusCancelled');
      default:
        return status;
    }
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const formatDate = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const statuses = ['DRAFT', 'ISSUED', 'PARTIALLY_APPLIED', 'FULLY_APPLIED', 'CANCELLED'];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('finance.creditNotes.title')}</h1>
          <p className="text-muted-foreground">{t('finance.creditNotes.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/trading/finance/credit-notes/new')}>
          <Plus className="mr-2 h-4 w-4" />
          {t('finance.creditNotes.addCreditNote')}
        </Button>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={tCommon('delete')}
        description={t('finance.creditNotes.deleteConfirm')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={cancelMutation.isPending}
      />

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('finance.creditNotes.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="border-border focus:border-foreground focus:ring-foreground/20 h-8 w-[180px] border focus:ring-1">
            <SelectValue placeholder={t('finance.creditNotes.selectStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('finance.creditNotes.allStatuses')}</SelectItem>
            {statuses.map((status) => (
              <SelectItem key={status} value={status}>
                {getStatusLabel(status)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && creditNotes.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileMinus className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('finance.creditNotes.noCreditNotes')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('finance.creditNotes.noCreditNotesDesc')}
          </p>
          <Button className="mt-4" onClick={() => navigate('/trading/finance/credit-notes/new')}>
            <Plus className="mr-2 h-4 w-4" />
            {t('finance.creditNotes.addCreditNote')}
          </Button>
        </div>
      )}

      {/* Credit Notes Table */}
      {!isLoading && !error && creditNotes.length > 0 && (
        <>
          <div className="border-border bg-card overflow-hidden border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.creditNotes.creditNoteNumber')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.creditNotes.customer')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {t('finance.creditNotes.status')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.creditNotes.issueDate')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.creditNotes.originalInvoice')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('finance.creditNotes.total')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('finance.creditNotes.remaining')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {tCommon('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {creditNotes.map((note) => {
                  const config = statusConfig[note.status] || statusConfig.DRAFT;
                  const StatusIcon = config?.icon ?? Clock;
                  const remaining = Number(note.total) - Number(note.appliedAmount);

                  return (
                    <tr key={note.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <Link
                          to={`/trading/finance/credit-notes/${note.id}`}
                          className="text-primary font-mono text-sm font-medium hover:underline"
                        >
                          {note.creditNoteNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <div className="text-sm font-medium">{note.customerName}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${config?.color ?? 'bg-gray-100 text-gray-700'}`}
                        >
                          <StatusIcon className="h-3 w-3" />
                          {getStatusLabel(note.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {note.creditDate ? formatDate(note.creditDate) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {note.invoiceId ? (
                          <Link
                            to={`/trading/finance/invoices/${note.invoiceId}`}
                            className="text-primary hover:underline"
                          >
                            {note.invoiceNumber}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium">
                        {formatCurrency(Number(note.total), note.currency)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium">
                        <span className={remaining > 0 ? 'text-blue-600' : 'text-green-600'}>
                          {formatCurrency(remaining, note.currency)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/trading/finance/credit-notes/${note.id}`)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              {tCommon('view')}
                            </DropdownMenuItem>
                            {note.status === 'DRAFT' && (
                              <>
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate(`/trading/finance/credit-notes/${note.id}/edit`)
                                  }
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  {tCommon('edit')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => issueMutation.mutate(note.id)}>
                                  <Send className="mr-2 h-4 w-4" />
                                  {t('finance.creditNotes.issue')}
                                </DropdownMenuItem>
                              </>
                            )}
                            {['ISSUED', 'PARTIALLY_APPLIED'].includes(note.status) &&
                              remaining > 0 && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate(`/trading/finance/credit-notes/${note.id}/apply`)
                                  }
                                >
                                  <Link2 className="mr-2 h-4 w-4" />
                                  {t('finance.creditNotes.applyToInvoice')}
                                </DropdownMenuItem>
                              )}
                            {note.status === 'DRAFT' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => handleDeleteNote(note.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  {tCommon('delete')}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {tCommon('showing')} {(pagination.page - 1) * pagination.limit + 1} -{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} {tCommon('of')}{' '}
                {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="border-border hover:bg-muted focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1 disabled:opacity-50"
                >
                  {tCommon('previous')}
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                  className="border-border hover:bg-muted focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1 disabled:opacity-50"
                >
                  {tCommon('next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
